<template>
    <div class="icon-picker" :class="{ 'icon-picker--open': isOpen }">
        <button
            type="button"
            class="icon-picker-trigger"
            @click="toggle"
            ref="trigger"
        >
            <span class="icon-picker-preview">
                <i :class="value || defaultClass" :style="color ? { color } : null"></i>
            </span>
            <span class="icon-picker-value">
                {{ value || $t('icon_picker.placeholder') }}
            </span>
            <i class="fal fa-chevron-down icon-picker-chevron"></i>
        </button>

        <portal to="icon-picker-portal" v-if="isOpen">
        <div class="icon-picker-panel" ref="panel" :style="panelStyle">
            <div class="icon-picker-colors">
                <span class="icon-picker-colors-label">{{ $t('icon_picker.color') }}</span>
                <div class="icon-picker-swatches">
                    <button
                        type="button"
                        class="icon-picker-swatch icon-picker-swatch--reset"
                        :class="{ 'icon-picker-swatch--active': !color }"
                        :title="$t('icon_picker.default_color')"
                        @click="pickColor('')"
                    >
                        <i class="fal fa-ban"></i>
                    </button>
                    <button
                        v-for="preset in colorPresets"
                        :key="preset"
                        type="button"
                        class="icon-picker-swatch"
                        :class="{ 'icon-picker-swatch--active': color && color.toLowerCase() === preset.toLowerCase() }"
                        :style="{ background: preset }"
                        :title="preset"
                        @click="pickColor(preset)"
                    ></button>
                    <label
                        class="icon-picker-swatch icon-picker-swatch--custom"
                        :title="$t('icon_picker.custom_color')"
                    >
                        <i class="fal fa-eye-dropper"></i>
                        <input
                            type="color"
                            :value="color || '#0891b2'"
                            @input="pickColor($event.target.value)"
                            class="icon-picker-swatch-input"
                        />
                    </label>
                </div>
            </div>
            <div class="icon-picker-tabs">
                <button
                    v-for="tab in tabs"
                    :key="tab.prefix"
                    type="button"
                    class="icon-picker-tab"
                    :class="{ 'icon-picker-tab--active': activePrefix === tab.prefix }"
                    @click="activePrefix = tab.prefix"
                >
                    <i :class="[tab.prefix, tab.sample]"></i>
                    <span>{{ $t('icon_picker.styles.' + tab.key) }}</span>
                </button>
            </div>

            <div class="icon-picker-search">
                <i class="fal fa-search icon-picker-search-icon"></i>
                <input
                    type="text"
                    v-model="search"
                    :placeholder="$t('icon_picker.search')"
                    class="icon-picker-search-input"
                    ref="searchInput"
                />
                <button
                    v-if="search"
                    type="button"
                    class="icon-picker-search-clear"
                    @click="search = ''"
                >
                    <i class="fal fa-times"></i>
                </button>
            </div>

            <div class="icon-picker-grid-wrap" @scroll="onScroll" ref="gridWrap">
                <div v-if="filteredIcons.length === 0" class="icon-picker-empty">
                    {{ $t('icon_picker.empty') }}
                </div>
                <div v-else class="icon-picker-grid">
                    <button
                        v-for="name in visibleIcons"
                        :key="name"
                        type="button"
                        class="icon-picker-item"
                        :class="{ 'icon-picker-item--active': name === currentIconName }"
                        :title="name"
                        @click="pick(name)"
                    >
                        <i
                            :class="[activePrefix, 'fa-' + name]"
                            :style="color && name === currentIconName ? { color } : null"
                        ></i>
                    </button>
                </div>
                <div v-if="visibleCount < filteredIcons.length" class="icon-picker-more">
                    {{ $t('icon_picker.more', { remaining: filteredIcons.length - visibleCount }) }}
                </div>
            </div>

            <div class="icon-picker-footer">
                <button
                    type="button"
                    class="icon-picker-clear"
                    :disabled="!value"
                    @click="clear"
                >
                    <i class="fal fa-trash-alt mr-1"></i>
                    {{ $t('icon_picker.clear') }}
                </button>
            </div>
        </div>
        </portal>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { nonBrandIcons, brandIcons } from '@/assets/fa-icons'

const TABS = [
    { key: 'light', prefix: 'fal', sample: 'fa-star' },
    { key: 'regular', prefix: 'far', sample: 'fa-star' },
    { key: 'solid', prefix: 'fas', sample: 'fa-star' },
    { key: 'duotone', prefix: 'fad', sample: 'fa-star' },
    { key: 'brands', prefix: 'fab', sample: 'fa-font-awesome' }
]

const COLOR_PRESETS = [
    '#0891b2',
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#ec4899',
    '#f97316',
    '#14b8a6',
    '#64748b',
    '#1f2937'
]

const PAGE_SIZE = 120

export default {
    name: 'IconPicker',
    props: {
        value: {
            type: String,
            default: ''
        },
        color: {
            type: String,
            default: ''
        },
        defaultClass: {
            type: String,
            default: 'fal fa-folder'
        }
    },
    data() {
        return {
            isOpen: false,
            search: '',
            activePrefix: 'fal',
            visibleCount: PAGE_SIZE,
            tabs: TABS,
            colorPresets: COLOR_PRESETS,
            panelStyle: {}
        }
    },
    computed: {
        currentIconName() {
            if (!this.value) return null
            const parts = this.value.trim().split(/\s+/)
            const iconClass = parts.find((p) => p.startsWith('fa-') && p !== 'fa-')
            return iconClass ? iconClass.replace(/^fa-/, '') : null
        },
        currentPrefix() {
            if (!this.value) return null
            const parts = this.value.trim().split(/\s+/)
            return parts.find((p) => ['fal', 'far', 'fas', 'fad', 'fab', 'fa'].includes(p)) || null
        },
        iconsForPrefix() {
            return this.activePrefix === 'fab' ? brandIcons : nonBrandIcons
        },
        filteredIcons() {
            const q = this.search.trim().toLowerCase()
            if (!q) return this.iconsForPrefix
            return this.iconsForPrefix.filter((name) => name.includes(q))
        },
        visibleIcons() {
            return this.filteredIcons.slice(0, this.visibleCount)
        }
    },
    watch: {
        search() {
            this.visibleCount = PAGE_SIZE
            this.$nextTick(() => {
                if (this.$refs.gridWrap) this.$refs.gridWrap.scrollTop = 0
            })
        },
        activePrefix() {
            this.visibleCount = PAGE_SIZE
            this.$nextTick(() => {
                if (this.$refs.gridWrap) this.$refs.gridWrap.scrollTop = 0
            })
        },
        isOpen(open) {
            if (open) {
                this.syncFromValue()
                this.updatePosition()
                this.$nextTick(() => {
                    this.updatePosition()
                    if (this.$refs.searchInput) this.$refs.searchInput.focus()
                })
                document.addEventListener('mousedown', this.onOutsideClick)
                window.addEventListener('scroll', this.updatePosition, true)
                window.addEventListener('resize', this.updatePosition)
            } else {
                document.removeEventListener('mousedown', this.onOutsideClick)
                window.removeEventListener('scroll', this.updatePosition, true)
                window.removeEventListener('resize', this.updatePosition)
            }
        }
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.onOutsideClick)
        window.removeEventListener('scroll', this.updatePosition, true)
        window.removeEventListener('resize', this.updatePosition)
    },
    methods: {
        toggle() {
            this.isOpen = !this.isOpen
        },
        syncFromValue() {
            if (this.currentPrefix && this.currentPrefix !== 'fa') {
                this.activePrefix = this.currentPrefix
            }
        },
        pick(name) {
            this.$emit('input', `${this.activePrefix} fa-${name}`)
            this.isOpen = false
        },
        pickColor(value) {
            this.$emit('update:color', value)
        },
        clear() {
            this.$emit('input', '')
            this.$emit('update:color', '')
            this.isOpen = false
        },
        onOutsideClick(e) {
            const insideTrigger = this.$el && this.$el.contains(e.target)
            const insidePanel = this.$refs.panel && this.$refs.panel.contains(e.target)
            if (!insideTrigger && !insidePanel) {
                this.isOpen = false
            }
        },
        updatePosition() {
            if (!this.isOpen || !this.$refs.trigger) return
            const rect = this.$refs.trigger.getBoundingClientRect()
            const vw = window.innerWidth
            const vh = window.innerHeight
            const panelHeight = (this.$refs.panel && this.$refs.panel.offsetHeight) || 420
            const gap = 6
            const edge = 8
            const spaceBelow = vh - rect.bottom
            const flip = spaceBelow < panelHeight + gap && rect.top > spaceBelow
            const width = Math.max(rect.width, 360)
            let left = rect.right - width
            if (left < edge) left = Math.min(rect.left, vw - width - edge)
            if (left < edge) left = edge
            const style = {
                position: 'fixed',
                left: left + 'px',
                width: width + 'px',
                zIndex: 10000
            }
            if (flip) {
                style.bottom = (vh - rect.top + gap) + 'px'
            } else {
                style.top = (rect.bottom + gap) + 'px'
            }
            this.panelStyle = style
        },
        onScroll(e) {
            const el = e.target
            if (
                el.scrollTop + el.clientHeight >= el.scrollHeight - 80 &&
                this.visibleCount < this.filteredIcons.length
            ) {
                this.visibleCount = Math.min(
                    this.visibleCount + PAGE_SIZE,
                    this.filteredIcons.length
                )
            }
        }
    }
}
</script>

<style scoped>
.icon-picker {
    position: relative;
    flex: 1;
}

.icon-picker-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 10px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.15s;
    font-size: 0.85rem;
    min-height: 34px;
}

.icon-picker-trigger:hover {
    border-color: #cbd5e1;
}

.icon-picker--open .icon-picker-trigger {
    border-color: #0891b2;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.08);
}

.icon-picker-preview {
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #0891b2;
}

.icon-picker-value {
    flex: 1;
    text-align: left;
    color: #334155;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.78rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.icon-picker-chevron {
    color: #94a3b8;
    font-size: 0.75rem;
    transition: transform 0.15s;
}

.icon-picker--open .icon-picker-chevron {
    transform: rotate(180deg);
}

.icon-picker-panel {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-width: 360px;
    max-width: calc(100vw - 16px);
}

.icon-picker-colors {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
    background: #fff;
}

.icon-picker-colors-label {
    font-size: 0.7rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
    flex-shrink: 0;
    padding-top: 3px;
}

.icon-picker-swatches {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    flex: 1;
}

.icon-picker-swatch {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1.5px solid transparent;
    cursor: pointer;
    padding: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.12s, border-color 0.12s;
}

.icon-picker-swatch:hover {
    transform: scale(1.15);
}

.icon-picker-swatch--active {
    border-color: #0f172a;
    box-shadow: 0 0 0 2px #fff inset;
}

.icon-picker-swatch--reset {
    background: #fff;
    border: 1.5px dashed #cbd5e1;
    color: #94a3b8;
    font-size: 0.55rem;
}

.icon-picker-swatch--reset.icon-picker-swatch--active {
    border-color: #0f172a;
    border-style: solid;
    box-shadow: none;
}

.icon-picker-swatch--custom {
    background: conic-gradient(#ef4444, #f59e0b, #10b981, #3b82f6, #8b5cf6, #ef4444);
    color: #fff;
    font-size: 0.55rem;
    border-color: rgba(255, 255, 255, 0.6);
}

.icon-picker-swatch-input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
}

.icon-picker-tabs {
    display: flex;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
}

.icon-picker-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 8px 4px;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    font-size: 0.68rem;
    color: #64748b;
    transition: all 0.15s;
}

.icon-picker-tab i {
    font-size: 1rem;
}

.icon-picker-tab:hover {
    color: #334155;
    background: #f1f5f9;
}

.icon-picker-tab--active {
    color: #0891b2;
    border-bottom-color: #0891b2;
    background: #fff;
}

.icon-picker-search {
    position: relative;
    padding: 8px;
    border-bottom: 1px solid #f1f5f9;
}

.icon-picker-search-icon {
    position: absolute;
    top: 50%;
    left: 18px;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.75rem;
    pointer-events: none;
}

.icon-picker-search-input {
    width: 100%;
    box-sizing: border-box;
    padding: 5px 28px 5px 28px;
    border: 1.5px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.8rem;
    line-height: 1.2;
    outline: none;
    transition: border-color 0.15s;
}

.icon-picker-search-input:focus {
    border-color: #0891b2;
    box-shadow: 0 0 0 2px rgba(8, 145, 178, 0.1);
}

.icon-picker-search-clear {
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e2e8f0;
    border: none;
    border-radius: 50%;
    color: #64748b;
    cursor: pointer;
    font-size: 0.55rem;
    padding: 0;
}

.icon-picker-search-clear:hover {
    background: #cbd5e1;
    color: #334155;
}

.icon-picker-grid-wrap {
    max-height: 280px;
    overflow-y: auto;
    padding: 8px;
}

.icon-picker-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
    gap: 4px;
}

.icon-picker-item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 1;
    min-height: 36px;
    background: transparent;
    border: 1.5px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    color: #475569;
    font-size: 0.95rem;
    transition: all 0.12s;
}

.icon-picker-item:hover {
    background: #f1f5f9;
    color: #0891b2;
    transform: translateY(-1px);
}

.icon-picker-item--active {
    background: rgba(8, 145, 178, 0.1);
    border-color: #0891b2;
    color: #0891b2;
}

.icon-picker-empty {
    text-align: center;
    padding: 30px 10px;
    color: #94a3b8;
    font-size: 0.85rem;
}

.icon-picker-more {
    text-align: center;
    padding: 8px;
    color: #94a3b8;
    font-size: 0.72rem;
    font-style: italic;
}

.icon-picker-footer {
    display: flex;
    justify-content: flex-end;
    padding: 8px 10px;
    border-top: 1px solid #f1f5f9;
    background: #f8fafc;
}

.icon-picker-clear {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: transparent;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.75rem;
    color: #64748b;
    cursor: pointer;
    transition: all 0.15s;
}

.icon-picker-clear:hover:not(:disabled) {
    background: #fee2e2;
    border-color: #fecaca;
    color: #dc2626;
}

.icon-picker-clear:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

@media (max-width: 640px) {
    .icon-picker-panel {
        min-width: auto;
    }

    .icon-picker-tab span {
        font-size: 0.62rem;
    }

    .icon-picker-grid {
        grid-template-columns: repeat(auto-fill, minmax(36px, 1fr));
    }
}
</style>
